import React, { useState, useRef } from 'react';
import { Phone, Mail, Calendar, FileText, Send, Paperclip, X, AlertCircle, CheckCircle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { emailService, type EmailAttachment } from '@/services/email';

interface ActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (activity: NewActivity) => void;
  prospectName: string;
  contactEmail?: string;
  organizationId?: string;
  contactId?: string;
}

export interface NewActivity {
  type: 'call' | 'email' | 'meeting' | 'note';
  description: string;
  date: Date;
  user: string;
  // Email specific
  subject?: string;
  recipients?: string[];
  body?: string;
  // Meeting specific
  meetingTitle?: string;
  startTime?: string;
  endTime?: string;
  attendees?: string[];
  location?: string;
}

// ==================== Log Call Modal ====================
export function LogCallModal({ isOpen, onClose, onSubmit, prospectName }: ActivityModalProps) {
  const [callType, setCallType] = useState<'outbound' | 'inbound'>('outbound');
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const description = `${callType === 'outbound' ? 'Outbound' : 'Inbound'} call${outcome ? ` - ${outcome}` : ''}${notes ? `: ${notes}` : ''}`;
    onSubmit({
      type: 'call',
      description,
      date: new Date(),
      user: 'John Doe', // In real app, get from auth context
    });
    // Reset form
    setCallType('outbound');
    setOutcome('');
    setNotes('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Log Call">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center gap-3 p-3 bg-pond/5 dark:bg-pond-light/10 rounded-lg">
          <Phone className="w-5 h-5 text-pond dark:text-pond-light" />
          <span className="text-sm text-slate dark:text-mist">
            Logging call with <strong>{prospectName}</strong>
          </span>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-2">
            Call Type
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setCallType('outbound')}
              className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                callType === 'outbound'
                  ? 'border-pond bg-pond/10 text-pond dark:border-pond-light dark:bg-pond-light/20 dark:text-pond-light'
                  : 'border-gray dark:border-night-pond text-chalkboard dark:text-rain-cloud hover:bg-pale-gray-1 dark:hover:bg-navy'
              }`}
            >
              Outbound
            </button>
            <button
              type="button"
              onClick={() => setCallType('inbound')}
              className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                callType === 'inbound'
                  ? 'border-pond bg-pond/10 text-pond dark:border-pond-light dark:bg-pond-light/20 dark:text-pond-light'
                  : 'border-gray dark:border-night-pond text-chalkboard dark:text-rain-cloud hover:bg-pale-gray-1 dark:hover:bg-navy'
              }`}
            >
              Inbound
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
            Outcome
          </label>
          <select
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            className="select"
          >
            <option value="">Select outcome</option>
            <option value="Connected">Connected</option>
            <option value="Left voicemail">Left voicemail</option>
            <option value="No answer">No answer</option>
            <option value="Busy">Busy</option>
            <option value="Wrong number">Wrong number</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What was discussed?"
            rows={3}
            className="input resize-none"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            <Phone className="w-4 h-4" />
            Log Call
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ==================== Send Email Modal ====================
export function SendEmailModal({ 
  isOpen, 
  onClose, 
  onSubmit: _onSubmit, 
  prospectName, 
  contactEmail,
  organizationId,
  contactId,
}: ActivityModalProps) {
  const [to, setTo] = useState(contactEmail || '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form when modal opens/closes
  const resetForm = () => {
    setTo(contactEmail || '');
    setSubject('');
    setBody('');
    setAttachments([]);
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      // Limit total attachments to 10 and size to 25MB each
      const validFiles = Array.from(files).filter(file => {
        if (file.size > 25 * 1024 * 1024) {
          setError(`File "${file.name}" exceeds 25MB limit`);
          return false;
        }
        return true;
      });
      
      setAttachments(prev => {
        const newFiles = [...prev, ...validFiles];
        if (newFiles.length > 10) {
          setError('Maximum 10 attachments allowed');
          return prev;
        }
        return newFiles;
      });
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setError(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Validate organization ID
    if (!organizationId) {
      setError('Organization ID is required to send emails');
      return;
    }

    setIsSending(true);
    
    try {
      // Convert files to attachments
      let emailAttachments: EmailAttachment[] = [];
      if (attachments.length > 0) {
        emailAttachments = await emailService.filesToAttachments(attachments);
      }

      // Send email via Exchange Online
      const result = await emailService.sendEmail({
        to: [to],
        subject,
        body,
        bodyType: 'text',
        attachments: emailAttachments,
        organizationId,
        contactId,
      });

      if (!result.success) {
        setError(result.error || 'Failed to send email');
        setIsSending(false);
        return;
      }

      // Success!
      setSuccess(true);
      
      // Note: We don't call onSubmit here because the server already creates
      // the activity record when sending the email. This prevents duplicates.
      
      // Close after a brief delay to show success
      setTimeout(() => {
        handleClose();
      }, 1500);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Send Email" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center gap-3 p-3 bg-pond/5 dark:bg-pond-light/10 rounded-lg">
          <Mail className="w-5 h-5 text-pond dark:text-pond-light" />
          <span className="text-sm text-slate dark:text-mist">
            Compose email to <strong>{prospectName}</strong>
          </span>
          <span className="ml-auto text-xs text-chalkboard dark:text-rain-cloud">
            via Exchange Online
          </span>
        </div>

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-rust/10 dark:bg-salmon/10 border border-rust/20 dark:border-salmon/20 rounded-lg text-rust dark:text-salmon text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Success message */}
        {success && (
          <div className="flex items-center gap-2 p-3 bg-turquoise/10 dark:bg-aqua/10 border border-turquoise/20 dark:border-aqua/20 rounded-lg text-turquoise dark:text-aqua text-sm">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <span>Email sent successfully!</span>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
            To
          </label>
          <input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="email@example.com"
            className="input"
            required
            disabled={isSending || success}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter subject"
            className="input"
            required
            disabled={isSending || success}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
            Message
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your message..."
            rows={8}
            className="input resize-none font-mono text-sm"
            required
            disabled={isSending || success}
          />
        </div>

        {/* Attachments */}
        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-2">
            Attachments
          </label>
          
          {/* File list */}
          {attachments.length > 0 && (
            <div className="mb-2 space-y-1">
              {attachments.map((file, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 p-2 bg-pale-gray-1 dark:bg-navy rounded-lg text-sm"
                >
                  <Paperclip className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                  <span className="flex-1 truncate text-slate dark:text-mist">{file.name}</span>
                  <span className="text-xs text-chalkboard dark:text-rain-cloud">
                    {formatFileSize(file.size)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    className="p-1 hover:bg-gray dark:hover:bg-night-pond rounded"
                    disabled={isSending || success}
                  >
                    <X className="w-3 h-3 text-chalkboard dark:text-rain-cloud" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Add attachment button */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            disabled={isSending || success || attachments.length >= 10}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary text-sm"
            disabled={isSending || success || attachments.length >= 10}
          >
            <Paperclip className="w-4 h-4" />
            Add Attachment
          </button>
          <span className="ml-2 text-xs text-chalkboard dark:text-rain-cloud">
            {attachments.length}/10 files (max 25MB each)
          </span>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button 
            type="button" 
            onClick={handleClose} 
            className="btn-secondary"
            disabled={isSending}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-primary" 
            disabled={isSending || success}
          >
            <Send className="w-4 h-4" />
            {isSending ? 'Sending...' : success ? 'Sent!' : 'Send Email'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ==================== Schedule Meeting Modal ====================
export function ScheduleMeetingModal({ isOpen, onClose, onSubmit, prospectName, contactEmail }: ActivityModalProps) {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [location, setLocation] = useState('');
  const [attendees, setAttendees] = useState(contactEmail || '');
  const [description, setDescription] = useState('');
  const [isScheduling, setIsScheduling] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsScheduling(true);
    
    // In real app, this would call the Exchange Online Calendar API
    // await createCalendarEventViaExchange({ title, date, startTime, endTime, attendees, description });
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const meetingDescription = `Meeting scheduled: "${title}" on ${date} at ${startTime}${location ? ` (${location})` : ''}`;
    
    onSubmit({
      type: 'meeting',
      description: meetingDescription,
      date: new Date(),
      user: 'John Doe',
      meetingTitle: title,
      startTime: `${date}T${startTime}`,
      endTime: `${date}T${endTime}`,
      attendees: attendees.split(',').map(a => a.trim()),
      location,
    });
    
    setIsScheduling(false);
    // Reset form
    setTitle('');
    setDate('');
    setStartTime('');
    setEndTime('');
    setLocation('');
    setAttendees(contactEmail || '');
    setDescription('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Schedule Meeting" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center gap-3 p-3 bg-pond/5 dark:bg-pond-light/10 rounded-lg">
          <Calendar className="w-5 h-5 text-pond dark:text-pond-light" />
          <span className="text-sm text-slate dark:text-mist">
            Schedule meeting with <strong>{prospectName}</strong>
          </span>
          <span className="ml-auto text-xs text-chalkboard dark:text-rain-cloud">
            via Exchange Calendar
          </span>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
            Meeting Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Discovery Call with Midwest Community Bank"
            className="input"
            required
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Start Time
            </label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="input"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              End Time
            </label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="input"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
            Location
          </label>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Microsoft Teams, Zoom, or physical address"
            className="input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
            Attendees (comma-separated emails)
          </label>
          <input
            type="text"
            value={attendees}
            onChange={(e) => setAttendees(e.target.value)}
            placeholder="email1@example.com, email2@example.com"
            className="input"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Meeting agenda..."
            rows={3}
            className="input resize-none"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={isScheduling}>
            <Calendar className="w-4 h-4" />
            {isScheduling ? 'Scheduling...' : 'Send Invite'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ==================== Add Note Modal ====================
export function AddNoteModal({ isOpen, onClose, onSubmit, prospectName }: ActivityModalProps) {
  const [note, setNote] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      type: 'note',
      description: note,
      date: new Date(),
      user: 'John Doe',
    });
    setNote('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Note">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center gap-3 p-3 bg-pond/5 dark:bg-pond-light/10 rounded-lg">
          <FileText className="w-5 h-5 text-pond dark:text-pond-light" />
          <span className="text-sm text-slate dark:text-mist">
            Add note for <strong>{prospectName}</strong>
          </span>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
            Note
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Enter your note..."
            rows={5}
            className="input resize-none"
            required
            autoFocus
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            <FileText className="w-4 h-4" />
            Save Note
          </button>
        </div>
      </form>
    </Modal>
  );
}
